// ==============================================
// AUTH CONTEXT
// ==============================================
// Single source of truth for the currently signed-in user and their roles.
// Wrap your app in <AuthProvider>, then use `const { user, roles, ... } = useAuth()`
// anywhere inside it.
//
// Loaded data per session:
//   - user         : Supabase auth user (from getSession)
//   - profile      : public.users row (is_admin, is_subscribed, onboarded, ...)
//   - affiliate    : public.affiliates row (or null) — auto-links by email
//                    on first login if the application was approved
//   - roles        : derived booleans { isAdmin, isAffiliate, isPremium }
//   - accessToken  : Supabase JWT for server API calls
//   - loading      : true while we're loading the initial session/profile
//
// Methods:
//   - refresh()    : force-reload profile/affiliate for the current session
//   - signOut()    : clears session and storage, navigates to /login

import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { supabase, supabaseUrl, supabaseAnonKey } from "../config/Supabase";

// Verbose tracing for auth state. Off in production builds; flip locally
// (or set REACT_APP_DEBUG_AUTH=1) when diagnosing a login/role issue.
const DEBUG_AUTH =
  process.env.NODE_ENV !== "production" &&
  process.env.REACT_APP_DEBUG_AUTH !== "0";
const dlog = (...args) => {
  if (DEBUG_AUTH) console.log(...args);
};

// Expose supabase on window so you can debug from the browser console, e.g.:
//   await window.__supabase.auth.getSession()
//   await window.__supabase.from("users").select("*").eq("id", "<uuid>").maybeSingle()
if (typeof window !== "undefined" && DEBUG_AUTH) {
  window.__supabase = supabase;
}

// Wrap a promise so it rejects after `ms` instead of hanging forever.
// Surfaces silent hangs as visible errors in the console.
const withTimeout = (promise, ms, label) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`[auth] ${label} timed out after ${ms}ms`)), ms)
    ),
  ]);

// Fetch the current user's profile via a SECURITY DEFINER RPC function.
// Using a direct table query on public.users returns 403 for some users due to
// a PostgREST/RLS interaction we haven't been able to resolve. The RPC runs as
// its owner (who has full table access) so it bypasses that restriction while
// still being safe — the function only returns the current auth user's own row.
const fetchMyProfile = async (accessToken) => {
  try {
    const resp = await fetch(`${supabaseUrl}/rest/v1/rpc/get_my_profile`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: supabaseAnonKey,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: "{}",
    });
    dlog("[auth] fetchMyProfile status:", resp.status);
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      return { data: null, error: { message: body.message || resp.statusText, status: resp.status } };
    }
    const data = await resp.json();
    return { data: data ?? null, error: null };
  } catch (err) {
    return { data: null, error: { message: err.message } };
  }
};

const AuthContext = createContext({
  user: null,
  profile: null,
  affiliate: null,
  roles: { isAdmin: false, isAffiliate: false, isPremium: false },
  accessToken: null,
  loading: true,
  profileReady: false,
  refresh: async () => {},
  signOut: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [affiliate, setAffiliate] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);
  // True once loadRoles has completed at least once for the current session.
  // Login.js waits for this before redirecting so roles are fully resolved.
  const [profileReady, setProfileReady] = useState(false);

  // Track the most recent loadRoles call so stale results never overwrite
  // newer state. Without this, an older concurrent call returning null could
  // wipe out the freshly-loaded admin profile.
  const loadCounter = useRef(0);

  // Load profile + affiliate for the given user. Resilient to transient
  // errors: never overwrites existing state with null on failure. State is
  // ONLY cleared by explicit signOut() (so spurious null-session events from
  // token refresh / visibility-change race conditions can't wipe our roles).
  const loadRoles = useCallback(async (sessionUser, accessToken) => {
    const callId = ++loadCounter.current;

    if (!sessionUser) {
      dlog("[auth] loadRoles: no user — keeping prior profile/affiliate (only signOut() clears)");
      return;
    }

    const t0 = performance.now();
    dlog("[auth] loadRoles call", callId, "for", sessionUser.email, "id:", sessionUser.id, "hasToken:", !!accessToken);

    const PROFILE_COLUMNS =
      "id,email,display_name,first_name,avatar_url,is_admin,is_subscribed,onboarded,subscription_status,phone,receive_email_notifications,receive_sms_notifications";

    let profileData = null;
    let profileErr = null;

    // ── profile via SECURITY DEFINER RPC ────────────────────────────────────
    // Direct table queries on public.users return 403 for some auth flows.
    // The RPC `get_my_profile` runs as its owner (bypassing RLS) but is safe —
    // it only returns the currently authenticated user's own row.
    try {
      dlog("[auth]   [", callId, "] calling get_my_profile RPC…");
      const tQ = performance.now();
      const token = accessToken || (await supabase.auth.getSession()).data?.session?.access_token;
      const res = await withTimeout(
        fetchMyProfile(token),
        8000,
        "get_my_profile RPC"
      );
      dlog("[auth]   [", callId, "] RPC done in", Math.round(performance.now() - tQ), "ms; data:", !!res.data, "err:", res.error?.message || "(none)");
      profileData = res.data;
      profileErr = res.error;
    } catch (e) {
      console.error("[auth]   [", callId, "] RPC FAILED:", e.message);
      profileErr = e;
    }

    if (callId !== loadCounter.current) {
      dlog("[auth] loadRoles call", callId, "is stale, discarding");
      return;
    }

    // ── affiliate row ───────────────────────────────────────────────────────
    let affiliateRes = { data: null, error: null };
    try {
      dlog("[auth]   [", callId, "] querying affiliates by auth_user_id…");
      const tQ = performance.now();
      affiliateRes = await withTimeout(
        supabase
          .from("affiliates")
          .select("*")
          .eq("auth_user_id", sessionUser.id)
          .maybeSingle(),
        8000,
        `affiliates.select by auth_user_id`
      );
      dlog("[auth]   [", callId, "] affiliates query done in", Math.round(performance.now() - tQ), "ms; data:", !!affiliateRes.data, "err:", affiliateRes.error?.message || "(none)");
    } catch (e) {
      console.error("[auth]   [", callId, "] affiliates query FAILED:", e.message);
    }

    if (callId !== loadCounter.current) return;

    // ── apply profile ───────────────────────────────────────────────────────
    if (profileErr) {
      console.warn("[auth]   [", callId, "] failed to load profile:", profileErr.message);
      // Do NOT clear existing profile on transient error.
    } else if (profileData) {
      dlog(
        "[auth]   [", callId, "] profile LOADED — is_admin:",
        profileData.is_admin,
        "is_subscribed:",
        profileData.is_subscribed,
        "onboarded:",
        profileData.onboarded
      );
      setProfile(profileData);
    } else {
      console.warn(
        "[auth]   [", callId, "] no public.users row found for auth user",
        sessionUser.id,
        sessionUser.email,
        "— is RLS blocking, or is there really no row?"
      );
      setProfile((prev) => prev || null);
    }

    // ── auto-link unclaimed affiliate by email ─────────────────────────────
    let affiliateRow = affiliateRes.data || null;

    if (!affiliateRow && sessionUser.email) {
      try {
        const { data: byEmail } = await withTimeout(
          supabase
            .from("affiliates")
            .select("*")
            .ilike("email", sessionUser.email)
            .is("auth_user_id", null)
            .maybeSingle(),
          8000,
          `affiliates.select unclaimed-by-email`
        );

        if (callId !== loadCounter.current) return;

        if (byEmail) {
          const { data: linked } = await withTimeout(
            supabase
              .from("affiliates")
              .update({ auth_user_id: sessionUser.id })
              .eq("id", byEmail.id)
              .select()
              .single(),
            8000,
            `affiliates.update auth_user_id`
          );
          affiliateRow = linked || byEmail;
        }
      } catch (e) {
        console.error("[auth]   [", callId, "] affiliate auto-link FAILED:", e.message);
      }
    }

    if (callId !== loadCounter.current) return;
    setAffiliate(affiliateRow);
    // Only mark ready if we actually got a profile back. If there was a
    // permission error (RLS not yet applied, etc.) keep profileReady=false so
    // Login.js doesn't redirect with empty roles.
    const isPermissionError =
      profileErr?.message?.includes("permission denied") ||
      profileErr?.code === "42501" ||
      profileErr?.message?.includes("timed out");
    if (!isPermissionError) {
      setProfileReady(true);
    } else {
      console.warn("[auth] profileReady NOT set — permission/timeout error prevented profile load");
    }
    dlog("[auth] loadRoles call", callId, "DONE in", Math.round(performance.now() - t0), "ms");
  }, []);

  // Apply a session. If `session` is null, this is a no-op — only signOut()
  // (or an explicit SIGNED_OUT event) is allowed to clear state. This prevents
  // transient null-session events (token refresh races, visibility-change
  // re-checks) from logging the user out mid-navigation.
  const applySession = useCallback(async (session) => {
    if (!session?.user) {
      dlog("[auth] applySession: null session — keeping current state");
      return;
    }
    setProfileReady(false);
    setUser(session.user);
    setAccessToken(session.access_token || null);
    await loadRoles(session.user, session.access_token);
  }, [loadRoles]);

  // Hard-clear everything. Called from signOut() and the SIGNED_OUT event.
  const clearAuthState = useCallback(() => {
    dlog("[auth] clearing auth state");
    setUser(null);
    setProfile(null);
    setAffiliate(null);
    setAccessToken(null);
    setProfileReady(false);
  }, []);

  const refresh = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    await applySession(session);
  }, [applySession]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!mounted) return;
        // For the very first load, an absent session DOES need to set the
        // (already-initial) null state — but we don't need to call setters
        // since defaults are already null.
        if (session?.user) await applySession(session);
      } catch (err) {
        console.warn("[auth] initial load failed:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        dlog("[auth] event:", event, "user:", session?.user?.email || "(none)");
        // INITIAL_SESSION fires right after we register and we already
        // handled it via getSession() above.
        if (event === "INITIAL_SESSION") return;
        // Only an explicit SIGNED_OUT may wipe our state.
        if (event === "SIGNED_OUT") {
          clearAuthState();
          return;
        }
        // CRITICAL: do NOT await Supabase queries from inside this callback.
        // GoTrue is still holding its internal LockManager lock when the
        // callback runs; calling `supabase.from(...).select(...)` here will
        // deadlock (the PostgREST request tries to read the session, which
        // needs the same lock that's still held). Defer to the next tick.
        // https://github.com/supabase/auth-js/issues/762
        setTimeout(() => {
          if (!mounted) return;
          applySession(session);
        }, 0);
      }
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe?.();
    };
  }, [applySession, clearAuthState]);

  const signOut = useCallback(async () => {
    dlog("[auth] sign-out");

    try { sessionStorage.setItem("just_signed_out", "1"); } catch {}

    clearAuthState();

    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith("sb-") || k.includes("supabase"))
        .forEach((k) => localStorage.removeItem(k));
    } catch {}

    try {
      supabase.auth.signOut({ scope: "local" }).catch(() => {});
    } catch {}

    window.location.replace(window.location.origin + "/login");
  }, [clearAuthState]);

  // Memoize so the object reference only changes when the underlying booleans
  // actually change. Without this, every AuthContext render creates a new object
  // which causes consumers' useEffect dep-arrays to fire on every re-render.
  const roles = useMemo(() => ({
    isAdmin: !!profile?.is_admin,
    isAffiliate: !!affiliate && (!affiliate.status || affiliate.status === "active"),
    isPremium: !!profile?.is_subscribed,
  }), [profile, affiliate]);

  const value = {
    user,
    profile,
    affiliate,
    roles,
    accessToken,
    loading,
    profileReady,
    refresh,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
